/**
 * Product Color Variants
 *
 * Handles two interactions tied to product color swatches:
 *   1. Color → Featured image swap (hover preview, click sticks).
 *      Works on:
 *        - single product page (.gallery__slider .swiper-slide:first-child img)
 *        - catalog product card (.product-card .card-post__img img)
 *        - shop product card (.shop-card .shop-card__image img)
 *        - shop product page (.shop-product__slider .swiper-slide:first-child img)
 *
 *   2. Price model → Color filter (single product page + shop product page).
 *      When the user selects a price model, color swatches whose
 *      `data-price-models` does NOT include the selected model are hidden.
 *      Colors with an empty `data-price-models` are considered available
 *      for all models.
 *
 * Data contract:
 *   - Color swatch element has:
 *       data-color="<name>"
 *       data-color-image="<url>"      (optional — without it, no swap)
 *       data-price-models="m1||m2|.." (|| separated list; empty = all models)
 *   - Price source (single product page): .js-product-price[data-price-model]
 *     (click filters colors)
 *   - Price source (shop product page): .js-product-price-select (select change filters colors)
 *   - The first image element inside the closest "host" (.js-card-color-host
 *     or the gallery slider) is the swap target. The host must have
 *     `data-default-image` so we can revert on hover-leave.
 */

const PRICE_MODELS_SEP = '||';
const FILTERED_HIDDEN_CLASS = 'is-filtered-hidden';
const SWATCH_HOVER_CLASS = 'is-hover-preview';
const SWATCH_ACTIVE_CLASS = 'is-active';

/**
 * Normalize a price-model token so comparisons are whitespace-insensitive:
 * trim leading/trailing whitespace and collapse internal runs of whitespace.
 * Guards against dirty data (e.g. a trailing space in the Prices repeater:
 * "ARMERIA " vs swatch "ARMERIA").
 */
function normalizeModel(value) {
	return String(value == null ? '' : value)
		.trim()
		.replace(/\s+/g, ' ');
}

function parsePriceModels(value) {
	if (!value) return [];
	return value
		.split(PRICE_MODELS_SEP)
		.map((v) => normalizeModel(v))
		.filter(Boolean);
}

function findSwapTarget(swatch) {
	// Catalog / shop card layout
	const cardHost = swatch.closest('.js-card-color-host');
	if (cardHost) {
		const wrap = cardHost.querySelector('.js-card-image-wrap') || cardHost;
		return {
			host: cardHost,
			img: wrap.querySelector('img'),
		};
	}

	// Single product page — first gallery slide image
	const singleGallery = document.querySelector('.js-single-product-gallery');
	if (singleGallery) {
		// Match swatches that live anywhere on the page (product-colors block)
		const img =
			singleGallery.querySelector('.js-single-featured-img') ||
			singleGallery.querySelector('.swiper-slide img');
		return { host: singleGallery, img };
	}

	// Shop product page — first slide of the shop gallery
	const shopGallery = document.querySelector('.js-shop-product-gallery');
	if (shopGallery) {
		const img = shopGallery.querySelector('.swiper-slide img');
		return { host: shopGallery, img };
	}

	return { host: null, img: null };
}

function swapImage(img, newSrc) {
	if (!img || !newSrc) return;
	// Strip srcset/sizes so the explicit src wins, then restore if reverting
	if (img.dataset.originalSrcset === undefined && img.srcset) {
		img.dataset.originalSrcset = img.srcset;
	}
	if (img.dataset.originalSizes === undefined && img.sizes) {
		img.dataset.originalSizes = img.sizes;
	}
	img.removeAttribute('srcset');
	img.removeAttribute('sizes');
	img.src = newSrc;
}

function revertImage(img, defaultSrc) {
	if (!img) return;
	const target = defaultSrc || img.dataset.defaultSrc || '';
	if (!target) return;
	img.src = target;
	if (img.dataset.originalSrcset) {
		img.srcset = img.dataset.originalSrcset;
	}
	if (img.dataset.originalSizes) {
		img.sizes = img.dataset.originalSizes;
	}
}

function getDefaultSrcFor(host, img) {
	if (host && host.dataset.defaultImage) return host.dataset.defaultImage;
	if (img && img.dataset.defaultSrc) return img.dataset.defaultSrc;
	return '';
}

/**
 * Wire image swap behavior for a single swatch.
 * Click → sticky swap (becomes the new "current" image).
 * Hover → temporary preview, leave reverts to the current sticky image.
 */
function bindImageSwap(swatch) {
	const newSrc = swatch.dataset.colorImage;
	if (!newSrc) return; // No image for this color — skip swap, but keep filter logic

	const { host, img } = findSwapTarget(swatch);
	if (!img) return;

	// Hover preview ONLY on hover-capable (non-touch) devices. On iOS Safari a
	// hover-bound element swallows the first tap as a hover (no click) and
	// reverts on tap-away, so the color never sticks. Gating hover behind
	// (hover: hover) lets a single tap fall straight through to 'click'.
	const hoverCapable = window.matchMedia && window.matchMedia('(hover: hover)').matches;
	if (hoverCapable) {
		swatch.addEventListener('mouseenter', () => {
			swatch.classList.add(SWATCH_HOVER_CLASS);
			swapImage(img, newSrc);
		});

		swatch.addEventListener('mouseleave', () => {
			swatch.classList.remove(SWATCH_HOVER_CLASS);
			// Revert to the currently SELECTED swatch's image (sticky). The active swatch lives
			// in the same swatch group as this swatch (not in the gallery host), so scope the
			// lookup to the swatch's own group; fall back to document, then to default image.
			const group = swatch.parentElement;
			const activeSwatch = (group && group.querySelector(`.${SWATCH_ACTIVE_CLASS}[data-color-image]`))
				|| document.querySelector(`.${SWATCH_ACTIVE_CLASS}[data-color-image]`);
			const stickySrc = activeSwatch ? activeSwatch.dataset.colorImage : getDefaultSrcFor(host, img);
			revertImage(img, stickySrc);
		});
	}

	// Click is ALWAYS wired (sticky select + swap) — works for tap and mouse.
	swatch.addEventListener('click', (e) => {
		// On card swatches inside an <a>, the host has onclick stopPropagation already
		const group = swatch.parentElement;
		if (group) {
			group.querySelectorAll(`.${SWATCH_ACTIVE_CLASS}`).forEach((s) => s.classList.remove(SWATCH_ACTIVE_CLASS));
		}
		swatch.classList.add(SWATCH_ACTIVE_CLASS);
		swapImage(img, newSrc);
	});
}

/**
 * Show/hide color swatches based on selected price model.
 * `selected` empty/null → show all.
 */
function filterColorsByPriceModel(swatchContainer, selected) {
	const selectedModel = normalizeModel(selected);
	const swatches = swatchContainer.querySelectorAll('[data-price-models]');
	swatches.forEach((swatch) => {
		const models = parsePriceModels(swatch.dataset.priceModels);
		const isUniversal = models.length === 0;
		const matches = isUniversal || !selectedModel || models.includes(selectedModel);

		if (matches) {
			swatch.classList.remove(FILTERED_HIDDEN_CLASS);
			swatch.removeAttribute('aria-hidden');
		} else {
			swatch.classList.add(FILTERED_HIDDEN_CLASS);
			swatch.setAttribute('aria-hidden', 'true');
		}
	});
}

/**
 * Wire the price → color filter for the single product page.
 * Trigger: click on .js-product-price (row), which sets the active model.
 */
function initSingleProductPriceFilter() {
	const priceRows = document.querySelectorAll('.js-product-prices .js-product-price');
	const colorContainer = document.querySelector('.js-product-colors');
	if (!priceRows.length || !colorContainer) return;

	// Initial: show all (no model preselected on single product page)
	filterColorsByPriceModel(colorContainer, '');

	priceRows.forEach((row) => {
		row.addEventListener('click', () => {
			const model = row.dataset.priceModel || '';
			const isActive = row.classList.contains('is-selected');

			priceRows.forEach((r) => r.classList.remove('is-selected'));

			if (isActive) {
				// Toggle off — show all
				filterColorsByPriceModel(colorContainer, '');
				return;
			}
			row.classList.add('is-selected');
			filterColorsByPriceModel(colorContainer, model);
		});
	});
}

/**
 * Wire the price → color filter for the shop product page (select + swatches).
 */
function initShopProductPriceFilter() {
	const select = document.querySelector('.js-product-price-select');
	const colorContainer = document.querySelector('.js-shop-color-swatches');
	if (!select || !colorContainer) return;

	const apply = () => {
		const selected = select.value || '';
		filterColorsByPriceModel(colorContainer, selected);
	};

	apply(); // initial state (also handles "Odaberite veličinu..." → empty → show all)
	select.addEventListener('change', apply);
}

/**
 * Bind image swap on every swatch in the page (works for all contexts).
 */
function initImageSwapAll() {
	const swatches = document.querySelectorAll(
		'.js-product-color, .js-shop-color-swatch, .js-card-color-swatch'
	);
	swatches.forEach(bindImageSwap);

	// Mark first swatch in each group active so revert-after-hover stays sticky
	document.querySelectorAll('.js-card-color-swatches, .js-shop-color-swatches').forEach((group) => {
		const first = group.querySelector('[data-color-image]');
		if (first && !group.querySelector(`.${SWATCH_ACTIVE_CLASS}`)) {
			// Don't auto-activate on cards — only on shop product page (shop swatches already get is-active in twig)
			// Cards keep the original featured image until user interacts.
		}
	});
}

export default function productColorVariants() {
	initImageSwapAll();
	initSingleProductPriceFilter();
	initShopProductPriceFilter();
}
